import { z } from 'zod';

export const tractInputSchema = z.object({
  name: z.string().min(1, 'required').max(255),
  state: z.string().min(2).max(64),
  county: z.string().min(1).max(128),
  description: z.string().max(10_000).nullable().optional(),
  township: z.string().max(64).nullable().optional(),
  range: z.string().max(64).nullable().optional(),
  section: z.string().max(64).nullable().optional(),
  block: z.string().max(64).nullable().optional(),
  lot: z.string().max(64).nullable().optional(),
  abstract: z.string().max(64).nullable().optional(),
  survey: z.string().max(255).nullable().optional(),
  apn: z.string().max(64).nullable().optional(),
  gross_acres: z.number().min(0).nullable().optional(),
  notes: z.string().max(10_000).nullable().optional(),
});
export type TractInputSchema = z.infer<typeof tractInputSchema>;

export const titleRunInputSchema = z.object({
  tract_id: z.string().uuid(),
  title: z.string().min(1).max(255),
  sovereignty_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  through_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  status: z.enum(['open', 'in_progress', 'under_review', 'complete']).optional(),
  remarks: z.string().max(10_000).nullable().optional(),
});
export type TitleRunInputSchema = z.infer<typeof titleRunInputSchema>;

export const entryInputSchema = z.object({
  title_run_id: z.string().uuid(),
  document_type_id: z.string().uuid().nullable().optional(),
  prospect_id: z.string().uuid().nullable().optional(),
  instrument_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  recording_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  book: z.string().max(64).nullable().optional(),
  page: z.string().max(64).nullable().optional(),
  instrument_number: z.string().max(128).nullable().optional(),
  legal_description: z.string().max(20_000).nullable().optional(),
  consideration: z.string().max(1024).nullable().optional(),
  interest_conveyed: z.string().max(1024).nullable().optional(),
  depth_limitation: z.string().max(1024).nullable().optional(),
  area_limitation: z.string().max(1024).nullable().optional(),
  reservations: z.string().max(10_000).nullable().optional(),
  township: z.string().max(64).nullable().optional(),
  range: z.string().max(64).nullable().optional(),
  section: z.string().max(64).nullable().optional(),
  block: z.string().max(64).nullable().optional(),
  abstract: z.string().max(64).nullable().optional(),
  survey: z.string().max(255).nullable().optional(),
  has_mineral_reservation: z.boolean().optional(),
  mineral_reservation_description: z.string().max(10_000).nullable().optional(),
  remarks: z.string().max(10_000).nullable().optional(),
  reviewer_notes: z.string().max(10_000).nullable().optional(),
});
export type EntryInputSchema = z.infer<typeof entryInputSchema>;

export const prospectInputSchema = z.object({
  name: z.string().min(1, 'required').max(255),
  description: z.string().max(10_000).nullable().optional(),
});
export type ProspectInputSchema = z.infer<typeof prospectInputSchema>;

const dateOpt = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD')
  .nullable()
  .optional();

const numericOpt = z.number().min(0).nullable().optional();

export const contractInputSchema = z
  .object({
    contract_type: z.enum(['row']),
    status: z.enum(['draft', 'active', 'in_renewal', 'expired', 'terminated']).optional(),
    title: z.string().min(1, 'required').max(500),
    contract_number: z.string().min(1).max(100).nullable().optional(),

    effective_date: dateOpt,
    execution_date: dateOpt,
    expiration_date: dateOpt,
    termination_date: dateOpt,
    renewal_notice_date: dateOpt,

    recorded: z.boolean().optional(),
    recording_state: z.string().max(100).nullable().optional(),
    recording_county: z.string().max(100).nullable().optional(),
    recording_volume: z.string().max(50).nullable().optional(),
    recording_page: z.string().max(50).nullable().optional(),
    recording_instrument_number: z.string().max(100).nullable().optional(),
    recording_date: dateOpt,

    consideration_amount: numericOpt,
    consideration_currency: z
      .string()
      .regex(/^[A-Z]{3}$/, 'ISO 4217 (3 uppercase letters)')
      .nullable()
      .optional(),
    consideration_notes: z.string().max(10_000).nullable().optional(),

    row_use: z.string().max(100).nullable().optional(),
    row_width_feet: numericOpt,
    row_length_feet: numericOpt,
    row_length_rods: numericOpt,
    row_pipeline_diameter_inches: numericOpt,
    row_pipeline_product: z.string().max(100).nullable().optional(),
    row_depth_restriction_feet: numericOpt,
    row_surface_restoration_required: z.boolean().nullable().optional(),

    township: z.string().max(64).nullable().optional(),
    range: z.string().max(64).nullable().optional(),
    section: z.string().max(64).nullable().optional(),
    block: z.string().max(64).nullable().optional(),
    lot: z.string().max(64).nullable().optional(),
    abstract: z.string().max(64).nullable().optional(),
    survey: z.string().max(255).nullable().optional(),
    apn: z.string().max(64).nullable().optional(),

    description: z.string().max(20_000).nullable().optional(),
    notes: z.string().max(20_000).nullable().optional(),
  })
  .refine(
    (v) =>
      !v.effective_date ||
      !v.expiration_date ||
      v.expiration_date >= v.effective_date,
    { message: 'expiration must be on or after effective date', path: ['expiration_date'] },
  );
export type ContractInputSchema = z.infer<typeof contractInputSchema>;

// Mirrors api/src/routes/leases.ts's `baseObject` (createSchema/updateSchema).
// updated_by_user_id is server-set only and intentionally excluded — never
// client-writable. reviewer_user_id IS writable here, but the server
// independently re-validates membership + assignment authorization (spec
// §2.4) rather than trusting this schema alone.
export const leaseInputSchema = z
  .object({
    lease_number: z.string().min(1, 'required').max(255),
    alt_lease_number: z.string().max(255).nullable().optional(),
    lease_type: z.string().max(100).nullable().optional(),
    lease_ownership: z.string().max(100).nullable().optional(),
    fed_rights: z.string().max(100).nullable().optional(),
    status: z.string().max(100).nullable().optional(),
    prospect: z.string().max(255).nullable().optional(),
    field: z.string().max(255).nullable().optional(),
    reviewer_user_id: z.string().uuid().nullable().optional(),

    lessor: z.string().max(500).nullable().optional(),
    lessee: z.string().max(500).nullable().optional(),

    county: z.string().max(128).nullable().optional(),
    state: z.string().max(64).nullable().optional(),
    township: z.string().max(64).nullable().optional(),
    range: z.string().max(64).nullable().optional(),
    section: z.string().max(64).nullable().optional(),
    block: z.string().max(64).nullable().optional(),

    lease_date: dateOpt,
    expire_date: dateOpt,
    term: z.number().int().nonnegative().nullable().optional(),

    book: z.string().max(50).nullable().optional(),
    page: z.string().max(50).nullable().optional(),
    entry: z.string().max(100).nullable().optional(),

    provided_description: z.string().max(20_000).nullable().optional(),
    description: z.string().max(20_000).nullable().optional(),

    gross_acres: numericOpt,
    net_acres: numericOpt,
    co_net_acres: numericOpt,

    orri: z.boolean().optional(),
    royalty: z.boolean().optional(),
    lease_on_minerals: z.boolean().optional(),
    depth_limitations: z.boolean().optional(),
    depth_limitations_remark: z.string().max(10_000).nullable().optional(),
    area_pugh: z.boolean().optional(),
    depth_pugh: z.boolean().optional(),

    remarks: z.string().max(20_000).nullable().optional(),
    reviewer_notes: z.string().max(20_000).nullable().optional(),
    change_remark: z.string().max(10_000).nullable().optional(),
  })
  .refine((v) => !v.lease_date || !v.expire_date || v.expire_date >= v.lease_date, {
    message: 'expire_before_lease_date',
    path: ['expire_date'],
  });
export type LeaseInputSchema = z.infer<typeof leaseInputSchema>;

// Mirrors api/src/routes/contracts-land.ts's `baseObject` (createSchema/
// updateSchema). Named contractLandInputSchema (not contractInputSchema —
// that name is taken by Running Title's `contracts`, an unrelated entity).
// updated_by_user_id is server-set only and intentionally excluded — never
// client-writable. reviewer_user_id IS writable here, but the server
// independently re-validates membership + assignment authorization (spec
// §2.4) rather than trusting this schema alone.
export const contractLandInputSchema = z.object({
  contract_number: z.string().min(1, 'required').max(255),
  contract_title: z.string().max(255).nullable().optional(),
  contract_type: z.string().max(100).nullable().optional(),
  prospect: z.string().max(255).nullable().optional(),
  field: z.string().max(255).nullable().optional(),
  reviewer_user_id: z.string().uuid().nullable().optional(),

  party_1: z.string().max(500).nullable().optional(),
  other_parties: z.string().max(2000).nullable().optional(),

  county: z.string().max(128).nullable().optional(),
  state: z.string().max(64).nullable().optional(),
  township: z.string().max(64).nullable().optional(),
  range: z.string().max(64).nullable().optional(),
  section: z.string().max(64).nullable().optional(),
  block: z.string().max(64).nullable().optional(),

  contract_date: dateOpt,

  book: z.string().max(50).nullable().optional(),
  page: z.string().max(50).nullable().optional(),
  entry: z.string().max(100).nullable().optional(),

  flag_for_issues: z.string().max(100).nullable().optional(),
  issue_remark: z.string().max(10_000).nullable().optional(),

  description: z.string().max(20_000).nullable().optional(),

  depth_limitations: z.boolean().optional(),
  depth_remarks: z.string().max(10_000).nullable().optional(),
  royalty: z.boolean().optional(),
  orri: z.boolean().optional(),
  non_consent: z.boolean().optional(),
  non_consent_penalty: z.string().max(100).nullable().optional(),
  call_on_production: z.boolean().optional(),
  special_pooling_provisions: z.boolean().optional(),
  special_pooling_remarks: z.string().max(10_000).nullable().optional(),
  gas_balancing: z.boolean().optional(),

  joa_form_type: z.string().max(255).nullable().optional(),
  change_of_operator: z.string().max(500).nullable().optional(),

  remarks: z.string().max(20_000).nullable().optional(),
  reviewer_notes: z.string().max(20_000).nullable().optional(),
  change_remarks: z.string().max(10_000).nullable().optional(),
});
export type ContractLandInputSchema = z.infer<typeof contractLandInputSchema>;

// Mirrors api/src/routes/wells.ts's `baseObject` (createSchema/updateSchema)
// — the writable `wells` columns from spec §2.1. updated_by_user_id is
// server-set only and intentionally excluded — never client-writable.
// reviewer_user_id IS writable here, but the server independently
// re-validates membership + assignment authorization (spec §2.4) rather
// than trusting this schema alone. The 16-number interest matrix +
// unit_acreage are nullable numbers (NUMERIC(12,10)/(12,2) columns,
// unconstrained here to mirror the API's `interestField`); production
// counts are nullable integers (`countField`).
const interestOpt = z.number().nullable().optional();
const countOpt = z.number().int().nullable().optional();

export const wellInputSchema = z.object({
  property_number: z.string().min(1, 'required').max(255),
  property_name: z.string().max(255).nullable().optional(),
  api: z.string().max(100).nullable().optional(),
  alt_prop_number: z.string().max(255).nullable().optional(),
  prospect: z.string().max(255).nullable().optional(),
  field: z.string().max(255).nullable().optional(),
  rsrv_cat: z.string().max(100).nullable().optional(),
  abstract: z.string().max(128).nullable().optional(),
  survey: z.string().max(128).nullable().optional(),
  roll_up: z.boolean().optional(),
  reviewer_user_id: z.string().uuid().nullable().optional(),

  well_status: z.string().max(100).nullable().optional(),
  well_type: z.string().max(100).nullable().optional(),
  unit_type: z.string().max(100).nullable().optional(),
  fed_rights: z.string().max(100).nullable().optional(),
  unit_acreage: interestOpt,
  do_number: z.string().max(100).nullable().optional(),
  owner: z.string().max(255).nullable().optional(),
  operator: z.string().max(255).nullable().optional(),

  county: z.string().max(128).nullable().optional(),
  state: z.string().max(64).nullable().optional(),
  township: z.string().max(64).nullable().optional(),
  range: z.string().max(64).nullable().optional(),
  section: z.string().max(64).nullable().optional(),
  block: z.string().max(64).nullable().optional(),

  description: z.string().max(20_000).nullable().optional(),
  flag_for_issues: z.string().max(100).nullable().optional(),
  exception_remark: z.string().max(10_000).nullable().optional(),
  title_issues: z.string().max(10_000).nullable().optional(),

  oil_bpo_wi: interestOpt,
  oil_apo_wi: interestOpt,
  oil_bpo_nri: interestOpt,
  oil_apo_nri: interestOpt,
  oil_bpo_orri: interestOpt,
  oil_apo_orri: interestOpt,
  oil_bpo_ri: interestOpt,
  oil_apo_ri: interestOpt,
  gas_bpo_wi: interestOpt,
  gas_apo_wi: interestOpt,
  gas_bpo_nri: interestOpt,
  gas_apo_nri: interestOpt,
  gas_bpo_orri: interestOpt,
  gas_apo_orri: interestOpt,
  gas_bpo_ri: interestOpt,
  gas_apo_ri: interestOpt,

  minerals: z.boolean().optional(),
  royalties: z.boolean().optional(),
  participating: z.boolean().optional(),
  non_participating: z.boolean().optional(),
  term: z.boolean().optional(),
  perpetual: z.boolean().optional(),
  call_on_prod: z.boolean().optional(),
  non_consent: z.boolean().optional(),
  non_consent_penalty: z.string().max(100).nullable().optional(),
  payout: z.boolean().optional(),
  payout_amount: z.string().max(100).nullable().optional(),
  payout_date: dateOpt,

  first_prod: dateOpt,
  last_prod: dateOpt,
  oil_data_as_of: dateOpt,
  oil_cum: countOpt,
  oil_avg_bpm_6mo: countOpt,
  oil_avg_bopd_6mo: countOpt,
  gas_data_as_of: dateOpt,
  gas_cum: countOpt,
  gas_avg_mcf_6mo: countOpt,
  gas_avg_mcfd_6mo: countOpt,
  num_completions: countOpt,
  total_depth: countOpt,
  prod_zones: z.string().max(255).nullable().optional(),

  remarks: z.string().max(20_000).nullable().optional(),
  reviewer_notes: z.string().max(20_000).nullable().optional(),
  change_remarks: z.string().max(10_000).nullable().optional(),
});
export type WellInputSchema = z.infer<typeof wellInputSchema>;

// Lease-tracts (api/src/routes/lease-tracts.ts, spec docs/superpowers/specs/
// 2026-08-16-land-data-lease-tracts-design.md §2.1/§3.1). Mirrors the API's
// `baseObject` exactly (updated_by_user_id is server-set, excluded; lease_id
// is context set once at create time, not part of this edit-form shape).
// Named leaseTractInputSchema (not tractInputSchema — that name is taken by
// Running Title's `tracts`, an unrelated entity in a different sub-app).
export const leaseTractInputSchema = z.object({
  agreement_number: z.string().min(1, 'required').max(255),
  type: z.string().max(100).nullable().optional(),
  status: z.string().max(100).nullable().optional(),
  reviewer_user_id: z.string().uuid().nullable().optional(),

  lessor: z.string().max(500).nullable().optional(),
  lessee: z.string().max(500).nullable().optional(),

  agreement_date: dateOpt,
  expiration_date: dateOpt,

  book: z.string().max(100).nullable().optional(),
  page: z.string().max(100).nullable().optional(),
  county: z.string().max(128).nullable().optional(),
  state: z.string().max(64).nullable().optional(),

  legal_description: z.string().max(20_000).nullable().optional(),
  legal_remark: z.string().max(10_000).nullable().optional(),
  all_depths: z.string().max(1_000).nullable().optional(),

  gross_acres: numericOpt,
  net_acres: numericOpt,

  royalty: interestOpt,
  orri_owner: z.string().max(255).nullable().optional(),
  orri_decimal: interestOpt,
  wi_owner: z.string().max(255).nullable().optional(),
  wi_decimal: interestOpt,
  other_burdens: z.string().max(10_000).nullable().optional(),
});
export type LeaseTractInputSchema = z.infer<typeof leaseTractInputSchema>;

// Lease-tract shared-acreage (api/src/lib/shared-acreage-router.ts, spec §3.2).
// Mirrors the API's `{id, label, remark}` target shape and `{target_tract_id,
// remark?}` POST body.
export const leaseTractSharedAcreageTargetSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
  remark: z.string().nullable(),
});
export type LeaseTractSharedAcreageTargetSchema = z.infer<
  typeof leaseTractSharedAcreageTargetSchema
>;

export const addLeaseTractSharedAcreageInputSchema = z.object({
  target_tract_id: z.string().uuid(),
  remark: z.string().max(2000).nullable().optional(),
});
export type AddLeaseTractSharedAcreageInputSchema = z.infer<
  typeof addLeaseTractSharedAcreageInputSchema
>;

// Land Data cross-references (api/src/lib/xref-router.ts, spec
// docs/superpowers/specs/2026-08-16-land-data-xrefs-design.md §3-4). Mirrors
// the API's `{type, target_id}` POST body and the `{id, label}` shape
// returned per related record.
export const xrefEntitySchema = z.enum(['lease', 'contract', 'well']);
export type XrefEntitySchema = z.infer<typeof xrefEntitySchema>;

export const xrefTargetSchema = z.object({
  id: z.string().uuid(),
  label: z.string(),
});
export type XrefTargetSchema = z.infer<typeof xrefTargetSchema>;

export const addXrefInputSchema = z.object({
  type: xrefEntitySchema,
  target_id: z.string().uuid(),
});
export type AddXrefInputSchema = z.infer<typeof addXrefInputSchema>;

// Obligations catalog (api/src/routes/obligations.ts, spec
// docs/superpowers/specs/2026-08-16-land-data-obligations-design.md §2.1).
// NO reviewer_user_id — mirrors the API's createSchema/updateSchema
// baseObject exactly (updated_by_user_id is server-set, excluded here).
export const obligationValueTypeSchema = z.enum(['none', 'date', 'days']);
export type ObligationValueTypeSchema = z.infer<typeof obligationValueTypeSchema>;

export const obligationInputSchema = z.object({
  name: z.string().min(1, 'required').max(255),

  applies_to_lease: z.boolean().optional(),
  applies_to_contract: z.boolean().optional(),
  applies_to_well: z.boolean().optional(),

  value_type: obligationValueTypeSchema.optional(),

  attention: z.string().max(255).nullable().optional(),
  address_1: z.string().max(255).nullable().optional(),
  address_2: z.string().max(255).nullable().optional(),
  city: z.string().max(128).nullable().optional(),
  state: z.string().max(64).nullable().optional(),
  zip: z.string().max(32).nullable().optional(),
  phone: z.string().max(64).nullable().optional(),
  fax: z.string().max(64).nullable().optional(),
  email: z.string().max(255).nullable().optional(),

  remarks: z.string().max(20_000).nullable().optional(),
});
export type ObligationInputSchema = z.infer<typeof obligationInputSchema>;

// Obligation assignments (api/src/lib/obligation-assignment-router.ts, spec
// §3.2/§4.2). `host` mirrors the API's ObligationTargetType — leases→'lease',
// contracts-land→'contract', wells→'well'.
export const obligationAssignmentHostSchema = z.enum(['lease', 'contract', 'well']);
export type ObligationAssignmentHostSchema = z.infer<typeof obligationAssignmentHostSchema>;

export const obligationAssignmentSchema = z.object({
  id: z.string().uuid(),
  obligation_id: z.string().uuid(),
  name: z.string(),
  value_type: obligationValueTypeSchema,
  date_value: z.string().nullable(),
  days_value: z.number().int().nullable(),
});
export type ObligationAssignmentSchema = z.infer<typeof obligationAssignmentSchema>;

export const addObligationAssignmentInputSchema = z.object({
  obligation_id: z.string().uuid(),
  date_value: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .optional(),
  days_value: z.number().int().min(0).nullable().optional(),
});
export type AddObligationAssignmentInputSchema = z.infer<typeof addObligationAssignmentInputSchema>;
